import { Edit, Lock, Plus, Trash2, User } from 'lucide-react';
import { useEffect, useMemo, useState, type FC, type JSX } from 'react';
import { toast } from '../../../lib/utils/toast';
import { getErrorMessage } from '../../../lib/utils/helpers';
import type { AdminUser, CreateAdminPayload } from '../../../services/api/admin/adminApi';
import type { Role } from '../../../services/api/roles/roleApi';
import {
  useCreateAdmin,
  useDeleteAdmin,
  useGetAdmins,
  useUpdateAdmin
} from '../../../services/query/admin/admin';
import { useGetRoles } from '../../../services/query/roles/role';
import Button from '../../shared/Button';
import ConfirmModal from '../../shared/ConfirmModal';
import FormField from '../../shared/FormField';
import Input from '../../shared/Input';
import Modal from '../../shared/Modal';
import Pagination from '../../shared/Pagination';
import Select from '../../shared/Select';

type AdminFormState = {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role_id: number | '';
  is_active: boolean;
  is_superuser: boolean;
};

const AdminUsersTab: FC = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<AdminUser | null>(null);

  const { data: adminsResponse, isLoading: adminsLoading } = useGetAdmins({ page, limit: 10 });
  const { data: rolesResponse } = useGetRoles({ page: 1, limit: 100 });

  const { mutate: createAdmin, isPending: creating } = useCreateAdmin();
  const { mutate: updateAdmin, isPending: updating } = useUpdateAdmin();
  const { mutate: deleteAdmin, isPending: deleting } = useDeleteAdmin();

  const admins = useMemo(() => adminsResponse?.data ?? [], [adminsResponse?.data]);
  const pagination = adminsResponse?.pagination;

  const roles = useMemo(() => rolesResponse?.data ?? [], [rolesResponse?.data]);

  const filteredAdmins = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return admins;

    return admins.filter((a) => {
      return (
        a.username.toLowerCase().includes(s) ||
        a.email.toLowerCase().includes(s) ||
        a.role_name?.toLowerCase().includes(s) ||
        `${a.first_name ?? ''} ${a.last_name ?? ''}`.toLowerCase().includes(s)
      );
    });
  }, [admins, searchTerm]);

  const openCreate = () => {
    setEditingAdmin(null);
    setShowForm(true);
  };

  const openEdit = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setShowForm(true);
  };

  const openDelete = (admin: AdminUser) => {
    setAdminToDelete(admin);
    setConfirmDeleteOpen(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingAdmin(null);
  };

  const closeDelete = () => {
    setConfirmDeleteOpen(false);
    setAdminToDelete(null);
  };

  const handleDelete = () => {
    if (!adminToDelete) return;

    deleteAdmin(adminToDelete.id, {
      onSuccess: () => {
        toast.success('Admin deleted');
        closeDelete();
      },
      onError: (error: unknown) => {
        toast.error(getErrorMessage(error, 'Failed to delete admin'));
      }
    });
  };

  const totalItems = pagination?.total_items ?? admins.length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Admins</h3>
            <User className="w-6 h-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalItems}</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Active Admins</h3>
            <Lock className="w-6 h-6 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {admins.filter((a) => a.is_active).length}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Superusers</h3>
            <Lock className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {admins.filter((a) => a.is_superuser).length}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Roles</h3>
            <Lock className="w-6 h-6 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{roles.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Admin User Management</h2>
          <Button leftIcon={<Plus className="w-5 h-5" />} onClick={openCreate} type="button">
            Add Admin
          </Button>
        </div>

        <div className="mb-6">
          <FormField label="Search">
            <Input
              type="text"
              placeholder="Search by username, email, name, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={adminsLoading}
            />
          </FormField>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Superuser</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAdmins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{admin.username}</div>
                    <div className="text-sm text-gray-500">{admin.email}</div>
                    <div className="text-xs text-gray-500">
                      {(admin.first_name || admin.last_name) ? `${admin.first_name ?? ''} ${admin.last_name ?? ''}`.trim() : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {admin.role_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        admin.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {admin.is_superuser ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Yes
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEdit(admin)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit"
                        type="button"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDelete(admin)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                        type="button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!adminsLoading && filteredAdmins.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No admins found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {pagination ? (
          <Pagination
            pagination={pagination}
            currentPage={page}
            onPageChange={setPage}
            itemName="admins"
          />
        ) : null}
      </div>

      <AdminFormModal
        open={showForm}
        onClose={closeForm}
        admin={editingAdmin}
        roles={roles}
        loading={creating || updating}
        onSubmit={(payload) => {
          if (editingAdmin) {
            updateAdmin(
              { id: editingAdmin.id, payload },
              {
                onSuccess: () => {
                  toast.success('Admin updated');
                  closeForm();
                },
                onError: (error: unknown) => {
                  toast.error(getErrorMessage(error, 'Failed to update admin'));
                }
              }
            );
          } else {
            createAdmin(payload as CreateAdminPayload, {
              onSuccess: () => {
                toast.success('Admin created');
                closeForm();
              },
              onError: (error: unknown) => {
                toast.error(getErrorMessage(error, 'Failed to create admin'));
              }
            });
          }
        }}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={closeDelete}
        onConfirm={handleDelete}
        title="Delete admin"
        description={`Delete admin "${adminToDelete?.username ?? ''}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
};

const AdminFormModal: FC<{
  open: boolean;
  onClose: () => void;
  admin: AdminUser | null;
  roles: Role[];
  loading: boolean;
  onSubmit: (payload: Partial<CreateAdminPayload>) => void;
}> = ({ open, onClose, admin, roles, loading, onSubmit }): JSX.Element => {
  const isEdit = Boolean(admin);

  const [form, setForm] = useState<AdminFormState>(() => {
    return {
      username: admin?.username ?? '',
      email: admin?.email ?? '',
      first_name: admin?.first_name ?? '',
      last_name: admin?.last_name ?? '',
      password: '',
      role_id: admin?.role_id ?? '',
      is_active: admin?.is_active ?? true,
      is_superuser: admin?.is_superuser ?? false
    };
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      username: admin?.username ?? '',
      email: admin?.email ?? '',
      first_name: admin?.first_name ?? '',
      last_name: admin?.last_name ?? '',
      password: '',
      role_id: admin?.role_id ?? '',
      is_active: admin?.is_active ?? true,
      is_superuser: admin?.is_superuser ?? false
    });
  }, [open, admin]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.username.trim() || !form.email.trim()) {
      toast.error('Username and email are required');
      return;
    }

    if (!form.role_id) {
      toast.error('Role is required');
      return;
    }

    if (!isEdit && !form.password) {
      toast.error('Password is required');
      return;
    }

    const payload: Partial<CreateAdminPayload> = {
      username: form.username.trim(),
      email: form.email.trim(),
      first_name: form.first_name || undefined,
      last_name: form.last_name || undefined,
      role_id: Number(form.role_id),
      is_active: form.is_active,
      is_superuser: form.is_superuser
    };

    if (!isEdit) payload.password = form.password;

    onSubmit(payload);
  };

  const formId = 'admin-user-form';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Admin' : 'Add Admin'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={loading}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Username" required>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={loading}
              required
            />
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={loading}
              required
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="First Name">
            <Input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              disabled={loading}
            />
          </FormField>

          <FormField label="Last Name">
            <Input
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              disabled={loading}
            />
          </FormField>
        </div>

        {!isEdit ? (
          <FormField label="Password" required>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              disabled={loading}
              required
            />
          </FormField>
        ) : null}

        <FormField label="Role" required>
          <Select
            value={form.role_id}
            onChange={(e) => setForm({ ...form, role_id: e.target.value ? Number(e.target.value) : '' })}
            disabled={loading}
            required
          >
            <option value="" disabled>
              Select role
            </option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <div className="font-medium text-gray-900">Active</div>
              <div className="text-sm text-gray-600">Allow login</div>
            </div>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              disabled={loading}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <div className="font-medium text-gray-900">Superuser</div>
              <div className="text-sm text-gray-600">All permissions</div>
            </div>
            <input
              type="checkbox"
              checked={form.is_superuser}
              onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              disabled={loading}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AdminUsersTab;
